import React, { useState, useEffect } from "react";
import { terminalAPI } from "../../services/api";
import "./POSReaderRegistration.css";

const POSReaderRegistration = ({ isOpen, onClose, onSuccess, locationId }) => {
  const [step, setStep] = useState("init"); // init, connecting, registering, success, error
  const [readerLabel, setReaderLabel] = useState("");
  const [availableReaders, setAvailableReaders] = useState([]);
  const [registeredReaders, setRegisteredReaders] = useState([]);
  const [selectedReader, setSelectedReader] = useState(null);
  const [error, setError] = useState(null);
  const [connectionToken, setConnectionToken] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get connection token on component mount
  useEffect(() => {
    if (isOpen) {
      getConnectionToken();
    }
  }, [isOpen]);

  // Get connection token from the server
  const getConnectionToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await terminalAPI.createConnectionToken();
      setConnectionToken(response.data.secret);
      setStep("connecting");
      setLoading(false);
    } catch (error) {
      console.error("Error getting connection token:", error);
      setError(
        error.userMessage || "Failed to get connection token. Please try again."
      );
      setStep("error");
      setLoading(false);
    }
  };

  // Find already registered readers
  const findRegisteredReaders = async () => {
    if (!locationId) {
      setError("Location ID is required to find registered readers");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get registered readers from the server
      const response = await terminalAPI.findRegisteredReaders(locationId);
      setRegisteredReaders(response.data.readers || []);

      // Proceed to discover new readers
      discoverReaders();
    } catch (error) {
      console.error("Error finding registered readers:", error);
      // Continue with discovery even if finding registered readers fails
      discoverReaders();
    }
  };

  // Discover available readers
  const discoverReaders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the Stripe Terminal API to discover readers
      const response = await terminalAPI.listReaders(locationId);

      if (!response.data || !response.data.readers) {
        throw new Error("No readers found in the response");
      }

      // Get readers from Stripe
      const stripeReaders = response.data.readers;

      // Combine discovered readers with registered readers
      const combinedReaders = [...stripeReaders];

      // Add registered readers that aren't already in the list
      registeredReaders.forEach((reader) => {
        const alreadyInList = combinedReaders.some((r) => r.id === reader.id);
        if (!alreadyInList) {
          combinedReaders.push({
            ...reader,
            isRegistered: true,
            status: reader.dbInfo ? reader.dbInfo.status : "unknown",
          });
        } else {
          // Mark readers that are already in the list as registered
          const index = combinedReaders.findIndex((r) => r.id === reader.id);
          if (index !== -1) {
            combinedReaders[index] = {
              ...combinedReaders[index],
              isRegistered: true,
              dbInfo: reader.dbInfo,
              status: reader.dbInfo
                ? reader.dbInfo.status
                : combinedReaders[index].status,
            };
          }
        }
      });

      setAvailableReaders(combinedReaders);
      setLoading(false);
      setStep("registering");
    } catch (error) {
      console.error("Error discovering readers:", error);
      setError(
        error.userMessage || "Failed to discover readers. Please try again."
      );
      setLoading(false);
    }
  };

  // Register the selected reader
  const registerReader = async () => {
    if (!selectedReader) {
      setError("Please select a reader to register");
      return;
    }

    if (!readerLabel.trim()) {
      setError("Please provide a label for the reader");
      return;
    }

    if (!locationId) {
      setError("Location ID is required");
      return;
    }

    // Check if the reader is already registered
    if (selectedReader.isRegistered) {
      setError(
        "This reader is already registered. Please select another reader."
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get location name from the selected reader or use a default
      let locationName = "Unknown Location";

      // If the reader has location information, use it
      if (selectedReader.location) {
        try {
          // Try to get the location name from the reader's location
          const locationResponse = await terminalAPI.getLocationById(
            selectedReader.location
          );
          if (
            locationResponse.data &&
            locationResponse.data.location &&
            locationResponse.data.location.display_name
          ) {
            locationName = locationResponse.data.location.display_name;
          }
        } catch (locErr) {
          console.log(`Error retrieving location details: ${locErr.message}`);
        }
      }

      // For already discovered readers, we don't need to use the registration_code
      // Instead, we can use the reader_id directly
      const response = await terminalAPI.registerReader({
        label: readerLabel,
        location_id: locationId,
        location_name: locationName,
        reader_id: selectedReader.id,
        device_type: selectedReader.device_type,
        ip_address: selectedReader.ip_address,
        model:
          selectedReader.device_type === "stripe_s700"
            ? "S700"
            : selectedReader.device_type || "S700",
      });

      setLoading(false);
      setStep("success");

      // Call the onSuccess callback with the registered reader
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error registering reader:", error);
      setError(
        error.userMessage || "Failed to register reader. Please try again."
      );
      setLoading(false);
    }
  };

  // Reset the form
  const resetForm = () => {
    setStep("init");
    setReaderLabel("");
    setSelectedReader(null);
    setAvailableReaders([]);
    setError(null);
    setConnectionToken(null);
  };

  // Close the modal
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Render different content based on the current step
  const renderContent = () => {
    switch (step) {
      case "init":
        return (
          <div className="pos-reader-init">
            <h2>Register New S700 Reader</h2>
            <p>
              This will help you connect a new Stripe Reader S700 to your
              account. Make sure your reader is powered on and connected to the
              same network.
            </p>
            <button
              className="pos-reader-primary-btn"
              onClick={getConnectionToken}
              disabled={loading}
            >
              {loading ? "Initializing..." : "Start Registration"}
            </button>
          </div>
        );

      case "connecting":
        return (
          <div className="pos-reader-connecting">
            <h2>Connecting to Stripe Terminal</h2>
            <div className="pos-reader-spinner"></div>
            <p>Establishing secure connection to Stripe Terminal...</p>
            <button
              className="pos-reader-primary-btn"
              onClick={findRegisteredReaders}
              disabled={loading}
            >
              {loading ? "Connecting..." : "Find Readers"}
            </button>
            <button
              className="pos-reader-secondary-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        );

      case "registering":
        return (
          <div className="pos-reader-registering">
            <h2>Register S700 Reader</h2>

            <div className="pos-reader-form">
              <div className="pos-reader-form-group">
                <label>Available Readers:</label>
                <div className="pos-reader-list">
                  {availableReaders.length === 0 ? (
                    <p className="pos-reader-no-devices">
                      No S700 readers found on the network. Make sure your
                      reader is powered on and connected to the same network.
                    </p>
                  ) : (
                    availableReaders.map((reader) => (
                      <div
                        key={reader.id}
                        className={`pos-reader-item ${
                          selectedReader && selectedReader.id === reader.id
                            ? "selected"
                            : ""
                        } ${reader.isRegistered ? "registered" : ""}`}
                        onClick={() => setSelectedReader(reader)}
                      >
                        <div className="pos-reader-item-info">
                          <span className="pos-reader-item-label">
                            {reader.label || "Unnamed Reader"}
                            {reader.isRegistered && (
                              <span className="pos-reader-registered-badge">
                                Already Registered
                              </span>
                            )}
                          </span>
                          <span className="pos-reader-item-ip">
                            {reader.ip_address || "IP not available"}
                          </span>
                        </div>
                        <div className="pos-reader-item-status">
                          <span
                            className={`pos-reader-status-indicator ${reader.status}`}
                          ></span>
                          {reader.status}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="pos-reader-form-group">
                <label htmlFor="reader-label">Reader Label:</label>
                <input
                  type="text"
                  id="reader-label"
                  value={readerLabel}
                  onChange={(e) => setReaderLabel(e.target.value)}
                  placeholder="Enter a name for this reader"
                  disabled={loading}
                />
                <p className="pos-reader-form-help">
                  This label will help you identify this reader in your account.
                </p>
              </div>
            </div>

            <div className="pos-reader-actions">
              <button
                className="pos-reader-primary-btn"
                onClick={registerReader}
                disabled={loading || !selectedReader}
              >
                {loading ? "Registering..." : "Register Reader"}
              </button>
              <button
                className="pos-reader-secondary-btn"
                onClick={findRegisteredReaders}
                disabled={loading}
              >
                Refresh
              </button>
              <button
                className="pos-reader-secondary-btn"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="pos-reader-success">
            <div className="pos-reader-success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2>Reader Registered Successfully!</h2>
            <p>
              Your Stripe Reader S700 has been successfully registered and is
              ready to use.
            </p>
            <button className="pos-reader-primary-btn" onClick={handleClose}>
              Done
            </button>
          </div>
        );

      case "error":
        return (
          <div className="pos-reader-error">
            <div className="pos-reader-error-icon">
              <i className="fas fa-times-circle"></i>
            </div>
            <h2>Registration Failed</h2>
            <p>{error || "An error occurred during registration."}</p>
            <div className="pos-reader-actions">
              <button className="pos-reader-primary-btn" onClick={resetForm}>
                Try Again
              </button>
              <button
                className="pos-reader-secondary-btn"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // If the modal is not open, don't render anything
  if (!isOpen) {
    return null;
  }

  return (
    <div className="pos-reader-registration-overlay">
      <div className="pos-reader-registration-modal">
        {error && step !== "error" && (
          <div className="pos-reader-error-message">{error}</div>
        )}
        {renderContent()}
      </div>
    </div>
  );
};

export default POSReaderRegistration;
